<script setup>
import { computed } from 'vue'
import { Scatter } from 'vue-chartjs'
import {
  Chart as ChartJS,
  Tooltip,
  Legend,
  PointElement,
  LinearScale,
  Title
} from 'chart.js'

ChartJS.register(Tooltip, Legend, PointElement, LinearScale, Title)

const props = defineProps({
  data: { type: Array, required: true },
  xMin: { type: Number, default: 60 },
  yMin: { type: Number, default: 70 },
  xMax: { type: Number, default: 100 },
  yMax: { type: Number, default: 100 }
})

const zone = computed(() => ({
  xMin: props.xMin,
  xMax: props.xMax,
  yMin: props.yMin,
  yMax: props.yMax
}))

const highlightZone = {
  id: 'highlightZone',
  beforeDraw(chart) {
    const { ctx, scales: { x, y } } = chart
    const z = zone.value

    const xStart = x.getPixelForValue(z.xMin)
    const xEnd   = x.getPixelForValue(z.xMax)
    const yStart = y.getPixelForValue(z.yMax)
    const yEnd   = y.getPixelForValue(z.yMin)

    ctx.save()
    ctx.fillStyle = 'rgba(0, 255, 0, 0.12)'
    ctx.fillRect(xStart, yStart, xEnd - xStart, yEnd - yStart)

    ctx.strokeStyle = 'rgba(0, 128, 0, 0.7)'
    ctx.lineWidth = 2
    ctx.strokeRect(xStart, yStart, xEnd - xStart, yEnd - yStart)
    ctx.restore()
  }
}

const pointLabels = {
  id: 'pointLabels',
  afterDatasetsDraw(chart) {
    const { ctx } = chart

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex)

      meta.data.forEach((point, i) => {
        const label = dataset.data[i].label
        if (!label) return

        const x = point.x + 8
        const y = point.y - 8

        ctx.save()
        ctx.font = '10px sans-serif'
        ctx.fillStyle = 'black'
        ctx.fillText(label, x, y)
        ctx.restore()
      })
    })
  }
}

function getColor(index, total) {
  const hue = Math.round((index / total) * 360)
  return `hsl(${hue}, 70%, 55%)`
}

const chartData = computed(() => {
  const colors = props.data.map((_, i) => getColor(i, props.data.length))
  return {
    datasets: [
      {
        label: '',
        data: props.data,
        pointBackgroundColor: colors,
        pointRadius: 6
      }
    ]
  }
})

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        title: () => '',
        label(context) {
          const raw = context.raw || {}
          const initials = raw.label || ''
          const x = Number(context.parsed.x).toFixed(1)
          const y = Number(context.parsed.y).toFixed(1)

          return [
            initials,
            `Conversion Rate: ${x}%`,
            `Acceptance Rate: ${y}%`
          ]
        }
      },
      bodyFont: { size: 12 },
      titleFont: { size: 12 }
    }
  },
  scales: {
    x: {
      min: 0,
      max: 100,
      title: {
        display: true,
        text: 'Conversion Rate',
        font: { size: 10 }
      },
      ticks: {
        font: { size: 10 },
        callback: (v) => Number(v).toFixed(1) + '%'
      }
    },
    y: {
      min: 0,
      max: 100,
      title: {
        display: true,
        text: 'Acceptance Rate',
        font: { size: 10 }
      },
      ticks: {
        font: { size: 10 },
        callback: (v) => Number(v).toFixed(1) + '%'
      }
    }
  }
}

</script>

<template>
  <div style="height: 340px;">
    <Scatter
      :data="chartData"
      :options="chartOptions"
      :plugins="[highlightZone, pointLabels]"
    />
  </div>
</template>